// 보스 정의(D3a, CONTENT-90D PRD 2.1·2.2, 지역5·6은 3개월 C2): 지역 1~6의 중간보스(n-5)·지역보스(n-10). 데이터만 — 행동은 game/boss.ts 원형 둘(근접 treant, 시전 ember)이
// 이 표의 수치·추가 패턴으로 움직인다. 렌더는 원형 모델(고목 수호자·잿불 대주술사 GLB)에 배율·색조만 바꿔 그린다(전용 모델은 아트 레인 후속).
// PRD 2.1: 중간보스 패턴 2 이상, 지역보스 패턴 3~4 + 페이즈 2. 원형 패턴(내려찍기·휩쓸기·소환 / 고리·균열·운석·소환)에
// 보스마다 새 경고 패턴을 하나 이상 더한다(extra). 지역보스는 체력 phase2 이하에서 phase2Extra가 더해지고 추가 패턴을 더 자주 쓴다.
import type { BossKey } from '../levels';

/** 원형 행동: treant = 쫓아와 근접 경고(내려찍기 원·휩쓸기 부채꼴), ember = 거리를 재며 시전(고리·균열 3줄·운석) */
export type BossArchetype = 'treant' | 'ember';
/**
 * 추가 경고 패턴.
 * charge = 영웅 쪽 직선 경고 뒤 그 줄을 따라 돌진 · quake = 자기 둘레 큰 원(지진) · bite3 = 세 갈래 부채꼴이 차례로 문다 ·
 * cross = 대각 십자 4줄 얼음 · spores = 영웅 자리와 네 대각에 독 포자 원 5개 ·
 * lance3 = 정면 창날 3줄이 좌→중→우 차례로 찌른다(근접형) · execute = 정면 넓은 한 줄, 경고가 길고 아프다(근접형) ·
 * vortex = 보스 둘레 고리에 소용돌이 원 6개(안쪽 틈이 안전, 시전형) · star = 보스에서 6갈래 별빛 줄(시전형)
 */
export type BossExtra = 'charge' | 'quake' | 'bite3' | 'cross' | 'spores' | 'lance3' | 'execute' | 'vortex' | 'star';

export interface BossDef {
  readonly key: BossKey;
  readonly name: string;
  readonly archetype: BossArchetype;
  /** mid = n-5 중간보스, final = n-10 지역보스 */
  readonly tier: 'mid' | 'final';
  /** 지역 번호 */
  readonly region: number;
  /** 몸 반경(m, 충돌·판정). 렌더 배율과 같이 늘어난다 */
  readonly radius: number;
  /** 렌더: 원형 모델 대비 크기 배율 */
  readonly scale: number;
  /** 렌더: 몸 색조(곱, 0xffffff = 원본)와 가장자리 발광 색 */
  readonly tint: number;
  readonly rim: number;
  /** 근접형 추적 속도(m/s). 시전형은 BOSS_EMBER.moveSpeed */
  readonly speed: number;
  /** 원형 피해 배율(스테이지 피해 배율과 따로 곱한다) */
  readonly dmg: number;
  /**
   * 공격 사이 재사용 대기 배율(생략 = 1). 피해 배율(dmg)을 못 건드리는 보스(지역1 c018 불변식)의 보스전 위협을 내리는 레버:
   * 평소 대기와 광폭 대기(원형 × FRENZY.cooldownMult)에 곱한다. 경고(텔레그래프) 시간은 그대로다.
   */
  readonly cooldownMult?: number;
  /** 소환 체력 비율과 몹 종류(MobKind 번호, 지역 몹 이름으로 보인다) */
  readonly summonAt: readonly number[];
  readonly summonKinds: readonly number[];
  /** 처음부터 쓰는 추가 패턴 */
  readonly extra: readonly BossExtra[];
  /** 페이즈 2 체력 비율(0 = 없음)과 그때 더하는 패턴 */
  readonly phase2: number;
  readonly phase2Extra: readonly BossExtra[];
  /** 한 줄 소개(결과·안내 카드) */
  readonly blurb: string;
}

const G = 0, S = 1, B = 2, R = 3; // MobKind 순서(근접·방패·돌진·원거리)

export const BOSS_DEFS: Readonly<Record<BossKey, BossDef>> = {
  // 지역1
  chieftain: { key: 'chieftain', name: '고블린 족장', archetype: 'treant', tier: 'mid', region: 1, radius: 1.5, scale: 0.62, tint: 0xc8e6a0, rim: 0xffb070, speed: 2.3, dmg: 0.75,
    summonAt: [0.5], summonKinds: [G, G, G, G, S], extra: ['charge'], phase2: 0, phase2Extra: [], blurb: '무리를 부르고 직선으로 돌진한다.' },
  treant: { key: 'treant', name: '고목 수호자', archetype: 'treant', tier: 'final', region: 1, radius: 2.4, scale: 1, tint: 0xffffff, rim: 0xffd27a, speed: 1.5, dmg: 1, cooldownMult: 1.12,
    summonAt: [0.7, 0.4], summonKinds: [G, G, G, G, G, G], extra: [], phase2: 0.5, phase2Extra: ['quake'], blurb: '체력이 절반을 넘게 깎이면 뿌리 지진을 일으킨다.' },
  // 지역2
  golem: { key: 'golem', name: '용암 골렘', archetype: 'treant', tier: 'mid', region: 2, radius: 2.0, scale: 0.82, tint: 0x8a5a48, rim: 0xff7a2a, speed: 1.2, dmg: 1.1,
    summonAt: [0.5], summonKinds: [G, G, B], extra: ['quake'], phase2: 0, phase2Extra: [], blurb: '느리지만 발밑을 크게 뒤흔든다.' },
  ember: { key: 'ember', name: '잿불 대주술사', archetype: 'ember', tier: 'final', region: 2, radius: 1.6, scale: 1, tint: 0xffffff, rim: 0xff8a3d, speed: 1.8, dmg: 1,
    summonAt: [0.7, 0.4], summonKinds: [R, R, G, G, G], extra: [], phase2: 0.5, phase2Extra: ['cross'], blurb: '절반 아래에서 불꽃 십자를 긋는다.' },
  // 지역3
  warden: { key: 'warden', name: '묘지기 기사', archetype: 'treant', tier: 'mid', region: 3, radius: 1.8, scale: 0.74, tint: 0x9fb4d6, rim: 0x9ad8ff, speed: 1.9, dmg: 1.05,
    summonAt: [0.5], summonKinds: [S, S, R, R], extra: ['charge'], phase2: 0, phase2Extra: [], blurb: '방패병을 부르고 묘지를 가로질러 돌진한다.' },
  lich: { key: 'lich', name: '서리 리치', archetype: 'ember', tier: 'final', region: 3, radius: 1.7, scale: 1.12, tint: 0xa8dcff, rim: 0x7fd8ff, speed: 1.6, dmg: 1.24,
    summonAt: [0.7, 0.4], summonKinds: [R, R, G, G, B], extra: ['cross'], phase2: 0.5, phase2Extra: ['spores'], blurb: '얼음 십자를 긋고, 절반 아래에서 서리 폭발을 흩뿌린다.' },
  // 지역4
  priest: { key: 'priest', name: '독안개 사제', archetype: 'ember', tier: 'mid', region: 4, radius: 1.5, scale: 0.92, tint: 0xa6e08e, rim: 0x9cff6a, speed: 1.8, dmg: 1.92,
    summonAt: [0.5], summonKinds: [R, R, G, G], extra: ['spores'], phase2: 0, phase2Extra: [], blurb: '독 포자를 영웅 둘레에 흩뿌린다.' },
  hydra: { key: 'hydra', name: '늪의 히드라', archetype: 'treant', tier: 'final', region: 4, radius: 2.8, scale: 1.22, tint: 0x7fb88a, rim: 0x9cff6a, speed: 1.3, dmg: 1.39,
    summonAt: [0.7, 0.4], summonKinds: [G, G, B, B, R], extra: ['bite3'], phase2: 0.5, phase2Extra: ['quake'], blurb: '세 머리가 차례로 물고, 절반 아래에서 꼬리로 못을 뒤흔든다.' },
  // 지역5(3개월 C2): 렌더 = 원형 모델(근접 = 고목 수호자, 시전 = 잿불 대주술사)의 모래·금 색조·크기 변형.
  // dmg·cooldownMult = 생존 시뮬(tools/sim/combat.ts bossSurvival) 설계 목표: 표준 손실 50~56%, 평균 한 방 10% 이상. 시전형은 한 방이 작은 패턴이 많아 dmg를 올리고 재사용 대기로 위협을 맞춘다
  captain: { key: 'captain', name: '경비대장', archetype: 'treant', tier: 'mid', region: 5, radius: 1.7, scale: 0.7, tint: 0xd9b56a, rim: 0x5ff0e0, speed: 2.0, dmg: 1.45,
    summonAt: [0.5], summonKinds: [S, S, G, G, R], extra: ['lance3'], phase2: 0, phase2Extra: [], blurb: '방패병을 부르고 창날 세 줄로 차례로 찌른다.' },
  sandlord: { key: 'sandlord', name: '모래 군주', archetype: 'ember', tier: 'final', region: 5, radius: 2.0, scale: 1.3, tint: 0xf0c060, rim: 0x48e8d8, speed: 1.5, dmg: 1.83, cooldownMult: 1.08,
    summonAt: [0.7, 0.4], summonKinds: [G, G, B, B, R], extra: ['vortex'], phase2: 0.5, phase2Extra: ['spores'], blurb: '둘레에 모래 소용돌이를 일으키고, 절반 아래에서 유사 구덩이를 흩뿌린다.' },
  // 지역6(스토리 엔딩): 흑요석·보라
  executioner: { key: 'executioner', name: '심연 집행자', archetype: 'treant', tier: 'mid', region: 6, radius: 1.9, scale: 0.86, tint: 0x5a4870, rim: 0xc070ff, speed: 1.8, dmg: 0.84,
    summonAt: [0.5], summonKinds: [S, S, B, R], extra: ['execute', 'charge'], phase2: 0, phase2Extra: [], blurb: '거대한 도끼로 정면을 길게 내려찍고, 처형장을 가로질러 돌진한다.' },
  abysslord: { key: 'abysslord', name: '심연의 군주', archetype: 'ember', tier: 'final', region: 6, radius: 2.3, scale: 1.45, tint: 0x4a3a66, rim: 0xd060ff, speed: 1.6, dmg: 1.46, cooldownMult: 1.45,
    summonAt: [0.75, 0.5, 0.25], summonKinds: [G, G, S, B, R, R], extra: ['star', 'cross'], phase2: 0.5, phase2Extra: ['vortex'], blurb: '여섯 갈래 별빛과 십자를 긋고, 절반 아래에서 공허 소용돌이를 부른다.' },
};

/** 지역 번호(1..6)의 중간보스·지역보스 키 */
export const REGION_BOSSES: readonly { readonly mid: BossKey; readonly final: BossKey }[] = [
  { mid: 'chieftain', final: 'treant' },
  { mid: 'golem', final: 'ember' },
  { mid: 'warden', final: 'lich' },
  { mid: 'priest', final: 'hydra' },
  { mid: 'captain', final: 'sandlord' },
  { mid: 'executioner', final: 'abysslord' },
];

/** 렌더·이벤트용 보스 번호(BOSS_KEYS 순서). 새 보스는 맨 뒤에 붙인다(variant 번호가 이 순서다) */
export const BOSS_KEYS: readonly BossKey[] = ['treant', 'ember', 'chieftain', 'golem', 'warden', 'lich', 'priest', 'hydra', 'captain', 'sandlord', 'executioner', 'abysslord'];
export function bossIndex(key: BossKey): number { return BOSS_KEYS.indexOf(key); }

/**
 * 추가 패턴 수치(원형 피해 기준 배율·경고 길이 틱·모양). 모두 경고 원·부채꼴·줄이 판정과 같은 모양이다(텔레그래프 = 판정).
 * 경고 길이는 원형 패턴(1.0~1.3초)보다 짧지 않게 둔다(반응 가능).
 */
export const BOSS_EXTRA = {
  charge: { windup: 60, length: 13, width: 2.6, dmg: 1.15, dashTicks: 18 },
  quake: { windup: 84, radius: 7.5, dmg: 0.9 },
  bite3: { windup: 66, gap: 20, range: 8.5, arcDeg: 52, spreadDeg: 48, dmg: 0.7 },
  cross: { windup: 78, length: 18, width: 2.0, dmg: 1.0 },
  spores: { windup: 72, radius: 2.0, ring: 3.4, dmg: 1.05 },
  // C2: 창날 3줄(좌→중→우, gap 틱 간격), 처형(경고 길고 넓은 한 줄), 소용돌이(보스 둘레 ring m에 원 6개 — 보스 발밑·고리 바깥이 안전), 별빛 6줄
  lance3: { windup: 60, gap: 16, length: 11, width: 2.2, spreadDeg: 30, dmg: 0.75 },
  execute: { windup: 96, length: 15, width: 3.4, dmg: 1.6 },
  vortex: { windup: 84, radius: 2.4, ring: 5.2, dmg: 1.0 },
  star: { windup: 84, length: 17, width: 1.8, dmg: 1.0 },
} as const;
